import { ButtonHTMLAttributes, ReactElement } from "react";

import styles from "./Button.module.scss";

type ButtonSize = "xs" | "sm" | "md" | "lg";
type ButtonCategory = "primary" | "secondary" | "tertiary";
type ButtonVariant = "brand" | "neutral" | "error" | "warning" | "success";

interface IButtonProps {
    /**
     * The optional button ID.
     */
    id?: string;

    /**
     * The button color scheme.
     */
    variant?: ButtonVariant;

    /**
     * The category of importance.
     */
    category?: ButtonCategory;

    /**
     * The size of the label and button.
     */
    size?: ButtonSize;

    /**
     * An ID to target in frontend tests.
     */
    dataCy?: string;

    /**
     * Whether to disable the button click event.
     */
    disabled?: boolean;

    /**
     * A callback to invoke when the button is clicked.
     */
    onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;

    /**
     * The label that will be rendered inside the button
     */
    label: string;

    /**
     * The type of the button
     */
    type?: ButtonHTMLAttributes<HTMLButtonElement>["type"]
}

const variantMapper: Record<ButtonVariant, string> = {
    "brand": styles.brand,
    "error": styles.error,
    "neutral": styles.neutral,
    "success": styles.success,
    "warning": styles.warning
} as const;

const categoryMapper: Record<ButtonCategory, string> = {
    "primary": styles.primary,
    "secondary": styles.secondary,
    "tertiary": styles.tertiary
} as const;

const sizeMapper: Record<ButtonSize, string> = {
    "lg": styles.lg,
    "md": styles.md,
    "sm": styles.sm,
    "xs": styles.xs
} as const;

/**
 * The button.
 * @param {IButtonProps} param The props used to render the button.
 * @returns The rendered component.
 */
function Button({
    id,
    variant = "brand",
    category = "primary",
    size = "md",
    type = "submit",
    label,
    dataCy,
    disabled,
    onClick
}: Readonly<IButtonProps>): ReactElement {
    return (
        <button
            onClick={e => onClick?.(e)}
            disabled={disabled}
            data-cy={dataCy}
            id={id}
            type={type}
            className={`${styles.button} ${variantMapper[variant]} ${categoryMapper[category]} ${sizeMapper[size]}`}
        >
            {label}
        </button>
    );
}

export {
    Button,
    ButtonCategory,
    ButtonSize,
    ButtonVariant,
    IButtonProps
};
